import { IonRouterOutlet, IonTabBar, IonTabButton, IonTabs, useIonModal } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, useRouteMatch } from 'react-router';
import { RootState } from '../App';
import { useGetFeedQuery } from '../api/FeedApi';
import { useGetNotificationsQuery } from '../api/NotificationApi';
import {
	useGetFavoritePostsQuery,
	useGetInstallationTimeZoneQuery,
	useGetPromotedQuery,
	useGetUpcomingEventsQuery
} from '../api/PostApi';
import { useGetOwnProfileQuery } from '../api/UserApi';
import {
	IconBusiness,
	IconCalendar,
	IconChatbox,
	IconFeed,
	IconNotifications,
	IconSettings
} from '../components/QuirkIcons';
import { ReleaseVersionPopup } from '../components/ReleaseVersionRefresh';
import { useLoggedIn } from '../hooks/useLoggedIn';
import { LoginState } from '../state/loginSlice';
import { AboutAlgorithm } from './AboutAlgorithm';
import { AboutMenu } from './AboutMenu';
import { AcceptTermsOfService } from './AcceptTermsOfService';
import { BusinessDetail } from './BusinessDetail';
import { CommunityQuestionSearch } from './CommunityQuestionSearch';
import { EditCommunityQuestion } from './EditCommunityQuestion';
import { EditEvent } from './EditEvent';
import { EditOwnProfile } from './EditOwnProfile';
import { EditPoll } from './EditPoll';
import { EditPost } from './EditPost';
import { EditSiteSettings } from './EditSiteSettings';
import { EmailLogin } from './EmailLogin';
import { EventCalendar } from './EventCalendar';
import { Feed } from './Feed';
import { ForgotPassword } from './ForgotPassword';
import { GettingStartedExperience } from './GettingStartedExperience';
import { HowToFormatPosts } from './HowToFormatPosts';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './LandingPage.module.css';
import { Login } from './Login';
import { LogoutPage } from './LogoutPage';
import { NotificationsPage } from './NotificationsPage';
import { PostDetail } from './PostDetail';
import { ProfilePage } from './ProfilePage';
import { ReplyComment, ReplyPost } from './ReplyComment';
import { SendUserFeedback } from './SendUserFeedback';
import { SettingsMenu } from './SettingsMenu';
import { TermsOfService } from './TermsOfService';
import { TopicPreferencesPage } from './TopicPreferences';
import { UserPosts } from './UserPosts';
import { AdminUserLoginReport } from './admin/AdminUserLoginReport';
import { AdminUserMenu } from './admin/AdminUserMenu';
import { AdminUserSearch } from './admin/AdminUserSearch';
import { CreateBusiness } from './admin/CreateBusiness';
import { ReportList } from './admin/ReportList';

import { QuirkIonPage } from '../components/QuirkIonPage';
import { QuirkLoading } from '../components/QuirkLoading';
import { resetFeedOrder } from '../state/feedOrderSlice';
import { BusinessSearch } from './BusinessSearch';
import { EditBusiness } from './EditBusiness';
import { EditBusinessPost } from './EditBusinessPost';
import { ResendRegistrationEmail } from './ResendRegistrationEmail';
import { Unsubscribe } from './Unsubscribe';
import { VerifyAccount } from './VerifyAccount';
import { AdminBusinessApprovalDetail } from './admin/AdminBusinessApprovalDetail';
import { AdminBusinessApprovals } from './admin/AdminBusinessApprovals';
import { AdminTelemetryReport } from './admin/AdminTelemetryReport';
import { RealUserOnly } from './admin/RealUserOnly';
import { EditMarketPlaceItem } from './EditMarketplaceItem.js';
import { dateDiffMilliseconds } from 'model';
import { usePreferences } from '../hooks/usePreferences.js';
import { MessagesPage } from './MessagesPage.js';
import { Badge } from '../components/Badge.js';
import { ConversationPage } from './ConversationPage.js';
import { SendEmailBlast } from './admin/SendEmailBlast.js';
import { StripePaymentHandler } from './StripePaymentHandler.js';
import {
	startPolling,
	MessagesState,
	computeTotalUnread,
	refreshSummary,
	stopPolling
} from '../state/messagesSlice.js';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { SubscriptionPage } from './Subscription.js';
import { FeatureGate } from '../components/FeatureGate.js';
import { useSubscriptionsEnabled } from '../hooks/useSubscriptionsEnabled.js';
import { SelectCommunityPage } from './SelectCommunityPage.js';
import { ImportRSSFeedPage } from './admin/ImportRSSFeed.js';

const GettingStarted = React.lazy(() =>
	import('./GettingStarted').then(module => ({ default: module.GettingStarted }))
);

const Signup = React.lazy(() => import('./Signup').then(module => ({ default: module.Signup })));
const ResetPassword = React.lazy(() => import('./ResetPassword').then(module => ({ default: module.ResetPassword })));

export const LandingPage: React.FC = () => {
	const match = useRouteMatch();
	const state: LoginState = useSelector<RootState, LoginState>(state => state.login);
	const loggedIn = useLoggedIn();
	const { data } = useGetOwnProfileQuery(state.user._id, { skip: !loggedIn.loggedIn });
	const requireTermsOfService = state.requireTermsOfService;
	const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

	const [experienceModalOpened, setExperienceModalOpened] = useState(false);
	let newNotificationCount = 0;
	const [presentGettingStarted, dismissGettingStarted] = useIonModal(GettingStartedExperience, {
		onDismiss: () => dismissGettingStarted()
	});
	const [lastNotificationViewTime] = usePreferences('lastNotificationViewTime');
	// This is true if the user is anon or real, but logged in.
	const { refetch } = useGetFeedQuery(undefined, { skip: !loggedIn.loggedIn });
	const { refetch: refetchPromoted } = useGetPromotedQuery(undefined, {
		skip: !loggedIn.loggedIn
	});
	const { refetch: refetchNotifications, data: notifications } = useGetNotificationsQuery(undefined, {
		refetchOnFocus: true,
		skip: !loggedIn.realUser
	});
	const { refetch: refetchUpcomingEvents } = useGetUpcomingEventsQuery(
		{},
		{ refetchOnFocus: true, skip: !loggedIn.loggedIn }
	);
	const { refetch: refetchFavoriteEvents } = useGetFavoritePostsQuery(
		{
			postType: 'event',
			dayBack: 90
		},
		{ refetchOnFocus: true, skip: !loggedIn.realUser }
	);
	// This has a side effect of setting a staticlly defined timezone for the app
	useGetInstallationTimeZoneQuery();

	useEffect(() => {
		if (
			loggedIn.realUser &&
			!requireTermsOfService &&
			!experienceModalOpened &&
			data?.experienceFlags &&
			!data?.experienceFlags.includes('welcomeMessage')
		) {
			setExperienceModalOpened(true);
			presentGettingStarted();
		}
	}, [data?.experienceFlags, loggedIn.realUser, experienceModalOpened, presentGettingStarted, requireTermsOfService]);
	const virtuoso = useRef(null);

	if (notifications) {
		for (let i = 0; i < notifications?.length; i++) {
			const { created } = notifications[i];
			if (!lastNotificationViewTime || dateDiffMilliseconds(lastNotificationViewTime, created) > 1) {
				newNotificationCount++;
			}
		}
	}

	// Direct messages polling
	useEffect(() => {
		dispatch(startPolling());
		return () => {
			dispatch(stopPolling());
		};
	}, [dispatch]);

	const messagesState = useSelector((state: { messages: MessagesState }) => state.messages);

	const subscriptionsEnabled = useSubscriptionsEnabled();

	if (requireTermsOfService && loggedIn.realUser) {
		return <AcceptTermsOfService />;
	}

	return (
		<>
			<ReleaseVersionPopup />
			<IonReactRouter>
				<IonTabs>
					<IonRouterOutlet id="nav">
						<Route exact path="/main">
							<Redirect to="/main/latest" />
						</Route>
						<Route exact path={`${match.url}/latest`}>
							<Feed ref={virtuoso} />
						</Route>
						<Route exact path={`${match.url}/settings/admin/reports`}>
							<ReportList />
						</Route>
						<Route exact path={`${match.url}/settings/admin/users`}>
							<AdminUserSearch />
						</Route>
						<Route exact path={`${match.url}/settings/admin/reports/usersByDay`}>
							<AdminUserLoginReport />
						</Route>
						<Route exact path={`${match.url}/settings/admin/reports/telemetry`}>
							<AdminTelemetryReport />
						</Route>
						<Route exact path={`${match.url}/settings/admin/useradmin/:userid`}>
							<AdminUserMenu />
						</Route>
						<Route exact path={`${match.url}/settings/admin/business/approvals`}>
							<AdminBusinessApprovals />
						</Route>
						<Route exact path={`${match.url}/settings/admin/business/approval/:businessid`}>
							<AdminBusinessApprovalDetail />
						</Route>
						<Route exact path={`${match.url}/latest/post/show/:postid/:expanded?/:commentid?`}>
							<PostDetail />
						</Route>
						<Route exact path={`${match.url}/latest/post/:postid/comment/reply/:commentid`}>
							<ReplyComment />
						</Route>
						<Route path={`${match.url}/latest/post/reply/:postid/:source`}>
							<ReplyPost />
						</Route>
						<Route path={`${match.url}/latest/basic/edit/:postid`}>
							<EditPost />
						</Route>
						<Route path={`${match.url}/communityQuestion`}>
							<CommunityQuestionSearch />
						</Route>
						<Route path={`${match.url}/latest/communityQuestion/edit/:postid/:question`}>
							<EditCommunityQuestion />
						</Route>
						<Route exact path={`${match.url}/latest/communityQuestion/edit/:postid/`}>
							<EditCommunityQuestion />
						</Route>
						<Route path={`${match.url}/latest/event/edit/:postid`}>
							<EditEvent />
						</Route>
						<Route path={`${match.url}/latest/marketplaceItem/edit/:postId`}>
							<EditMarketPlaceItem />
						</Route>
						<Route path={`${match.url}/latest/poll/edit/:postid`}>
							<EditPoll />
						</Route>
						<Route exact path={`${match.url}/bus`}>
							<BusinessSearch />
						</Route>
						<Route exact path={`${match.url}/events`}>
							<EventCalendar />
						</Route>
						<Route exact path={`${match.url}/latest/bus/:businessid/edit/:postid`}>
							<RealUserOnly>
								<EditBusinessPost />
							</RealUserOnly>
						</Route>
						<Route exact path={`${match.url}/bus/:type(edit|approve)/:businessid`}>
							<RealUserOnly>
								<EditBusiness />
							</RealUserOnly>
						</Route>
						<Route exact path={`${match.url}/bus/show/:businessid`}>
							<BusinessDetail />
						</Route>
						<Route exact path={`${match.url}/bus/create/`}>
							<CreateBusiness />
						</Route>
						<Route exact path={'/main/settings/admin/importRSSFeed'}>
							<ImportRSSFeedPage />
						</Route>
						<Route exact path={`${match.url}/settings/userprofile/:id`}>
							<ProfilePage />
						</Route>
						<Route exact path={`${match.url}/latest/:id`}>
							<UserPosts />
						</Route>
						<Route exact path={`${match.url}/settings`}>
							<SettingsMenu />
						</Route>
						<Route exact path={`${match.url}/settings/profile`}>
							<EditOwnProfile />
						</Route>
						<Route path={`${match.url}/settings/payments/stripe/:status/:stripeSession/:userLedgerId`}>
							<StripePaymentHandler />
						</Route>

						<Route path={`${match.url}/settings/payments/stripe/:status/:stripeSession/:userLedgerId`}>
							<StripePaymentHandler />
						</Route>
						<Route exact path={`${match.url}/settings/topics/:topic?`}>
							<TopicPreferencesPage />
						</Route>
						<Route exact path={`${match.url}/settings/about`}>
							<AboutMenu />
						</Route>
						<Route exact path={`${match.url}/settings/about/sendfeedback`}>
							<SendUserFeedback />
						</Route>
						<Route exact path={`${match.url}/settings/about/terms`}>
							<TermsOfService />
						</Route>
						<Route exact path={`${match.url}/settings/about/algorithm`}>
							<AboutAlgorithm />
						</Route>
						<Route exact path={`${match.url}/settings/about/gettingstarted`}>
							<Suspense
								fallback={
									<QuirkIonPage>
										<QuirkLoading isLoading={true} />
									</QuirkIonPage>
								}
							>
								<GettingStarted />
							</Suspense>
						</Route>
						<Route exact path={`${match.url}/settings/about/howtoformatposts`}>
							<HowToFormatPosts />
						</Route>
						<Route exact path={`${match.url}/settings/admin/siteSettings`}>
							<EditSiteSettings />
						</Route>
						<FeatureGate role="subscription" hide={!subscriptionsEnabled}>
							<Route exact path={`${match.url}/settings/subscription`}>
								<SubscriptionPage />
							</Route>
						</FeatureGate>
						<Route exact path={`${match.url}/settings/admin/emailBlast`}>
							<SendEmailBlast />
						</Route>
						<Route exact path={`${match.url}/notifications`}>
							<NotificationsPage />
						</Route>

						<Route exact path={`${match.url}/messages`}>
							<MessagesPage />
						</Route>
						<Route exact path={`${match.url}/messages/:userId/:messageId?`}>
							<ConversationPage />
						</Route>
						<Route exact path={`${match.url}/settings/community`}>
							<SelectCommunityPage />
						</Route>
						<Route exact path={`${match.url}/settings/login`}>
							<Login />
						</Route>
						<Route exact path={`${match.url}/settings/logout`}>
							<LogoutPage />
						</Route>

						<Route exact path={`${match.url}/settings/signup`}>
							<Suspense
								fallback={
									<QuirkIonPage>
										<QuirkLoading isLoading={true} />
									</QuirkIonPage>
								}
							>
								<Signup />
							</Suspense>
						</Route>
						<Route exact path={`${match.url}/settings/register/:token`}>
							<VerifyAccount />
						</Route>
						<Route path={`${match.url}/settings/unsubscribe/:token"`}>
							<Unsubscribe />
						</Route>
						<Route exact path={`${match.url}/settings/resend`}>
							<ResendRegistrationEmail />
						</Route>
						<Route exact path={`${match.url}/settings/forgotPassword`}>
							<ForgotPassword />
						</Route>
						<Route path={`${match.url}/settings/emailLogin/:token`}>
							<EmailLogin />
						</Route>
						<Route path={`${match.url}/settings/resetPassword/:token`}>
							<Suspense
								fallback={
									<QuirkIonPage>
										<QuirkLoading isLoading={true} />
									</QuirkIonPage>
								}
							>
								<ResetPassword />
							</Suspense>
						</Route>
					</IonRouterOutlet>

					<IonTabBar slot="bottom" className={styles.IonTabBar}>
						<IonTabButton
							className={styles.IonTabButton}
							onClick={() => {
								dispatch(resetFeedOrder());
								refetch();
								refetchPromoted();
								(virtuoso?.current as any)?.scrollToIndex({ index: 0, behavior: 'smooth' });
							}}
							tab="tab1"
							href={`${match.url}/latest`}
							data-testid="navigationFeedButton"
						>
							<IconFeed />
						</IonTabButton>
						<IonTabButton
							className={styles.IonTabButton}
							tab="tab2"
							href={`${match.url}/messages`}
							data-testid="navigationMessagesButton"
							onClick={() => dispatch(refreshSummary())}
						>
							<Badge content={computeTotalUnread(messagesState) || undefined} type="alert" small>
								<IconChatbox />
							</Badge>
						</IonTabButton>
						<IonTabButton
							className={styles.IonTabButton}
							tab="tab3"
							href={`${match.url}/bus`}
							data-testid="navigationBusinessesButton"
						>
							<IconBusiness />
						</IonTabButton>
						<IonTabButton
							onClick={() => {
								try {
									refetchUpcomingEvents();
									refetchFavoriteEvents();
								} catch (e) {
									// Do nothing, as they might not have been fetched yet
								}
							}}
							className={styles.IonTabButton}
							tab="tab4"
							href={`${match.url}/events`}
							data-testid="navigationEventsButton"
						>
							<IconCalendar />
						</IonTabButton>
						<IonTabButton
							onClick={() => {
								try {
									refetchNotifications();
								} catch (e) {
									// do nothing, as they might not have been fetched yet
								}
							}}
							className={styles.IonTabButton}
							tab="tab5"
							href={`${match.url}/notifications`}
							data-testid="navigationNotificationsButton"
						>
							<Badge content={newNotificationCount || undefined} type="alert" small>
								<IconNotifications />
							</Badge>
						</IonTabButton>

						<IonTabButton
							className={styles.IonTabButton}
							tab="tab6"
							href={`${match.url}/settings/`}
							data-testid="navigationSettingsButton"
						>
							<IconSettings />
						</IonTabButton>
					</IonTabBar>
				</IonTabs>
			</IonReactRouter>
		</>
	);
};
